import { QueryExpressionParserService, SimpleChipExpression, ExpertChipExpression } from '@nrwl/query-emitter';
import { Injectable } from '@angular/core';
import { SimplifiedChipsGrammar } from './grammars/simplified.chips.grammar.type';
import { ExpertGrammar } from './grammars/expert.grammar.type';
import { GrammarHelper } from './grammar.helper';
import { find, isArray, map } from 'lodash';
declare let require: any;
const simplifiedGrammarParser = require('./grammars/simplified.chips.grammar');
const expertGrammarParser = require('./grammars/expert.grammar');

export enum SupportedSearchKeywords {
  App = 'app',
  Location = 'location',
  Site = 'site',
  Server = 'server',
  User = 'user',
  Host = 'host'
}

@Injectable()
export class AtwebQueryExpressionParserService extends QueryExpressionParserService {
  supportedSearchKeywords: Array<string>;

  constructor() {
    super();
  }

  setSupportedSearchKeywords(keywords) {
    this.supportedSearchKeywords = keywords || Object.keys(SupportedSearchKeywords);
  }

  public isChipExpressionValid(SimpleChipExpression: string): boolean {
    try {
      const kTree = simplifiedGrammarParser.parse(SimpleChipExpression, {
        types: SimplifiedChipsGrammar
      });
      const explanation = kTree.explain();
      return Boolean(this.areChipKeywordValid(explanation) && GrammarHelper.allExpressionFieldsPopulated(explanation));
    } catch (error) {
      return false; // normally we don't want to print error details
    }
  }

  public parseChipIntoTokens(SimpleChipExpression: string): SimpleChipExpression {
    try {
      const kTree = simplifiedGrammarParser.parse(SimpleChipExpression, {
        types: SimplifiedChipsGrammar
      });
      const explanation = kTree.explain();
      // normalize value of 'operands' - in case it is not an array, replace value with array.
      return GrammarHelper.normalizeSimpleChipExpression(explanation);
    } catch (error) {
      return null; // normally we don't want to print error details
    }
  }

  public getChipKeywords(): string[] {
    // ideally, we'd need to parse them from grammar file. not sure if list is going to change often, so just
    // hardcode them here.
    return this.supportedSearchKeywords;
  }

  public getChipOperators(): string[] {
    return ['=', '!='];
  }

  public isExpressionValid(expression: string): boolean {
    try {
      const kTree = expertGrammarParser.parse(expression, {
        types: ExpertGrammar
      });
      const explanation = kTree.explain();
      // must be an array
      if (isArray(explanation)) {
        const notValid = find(explanation, subExpression => !GrammarHelper.allExpressionFieldsPopulated(subExpression));
        return this.areKeywordsValid(explanation) && !notValid;
      } else {
        if (this.areChipKeywordValid(explanation) && GrammarHelper.allExpressionFieldsPopulated(explanation)) {
          return true;
        }
      }
    } catch (error) {
      return false; // normally we don't want to print error details
    }
    return false;
  }

  private areKeywordsValid(chips) {
    return chips.every(this.areChipKeywordValid.bind(this));
  }

  public areChipKeywordValid(chip) {
    return this.supportedSearchKeywords.find(expl => expl.toLowerCase() === chip.keyword);
  }

  public fitsWithUseCaseConstraints(chips: SimpleChipExpression[]): boolean {
    if (!chips) {
      return true;
    }
    // business logic quirks:
    // a) User and host cannot be specified together in the same free form filter.
    // b) Only one instance of a user or host is allowed.
    let hostChipCount = 0;
    let userChipCount = 0;
    for (const chip of chips) {
      if (chip.keyword === SupportedSearchKeywords.Host) {
        hostChipCount++;
      } else if (chip.keyword === SupportedSearchKeywords.User) {
        userChipCount++;
      }
    }
    return !(hostChipCount > 1 || userChipCount > 1 || hostChipCount + userChipCount > 1);
  }

  escapeSpecialJsonCharacters(input) {
    if (!input) {
      return input;
    }
    const output = JSON.stringify(input);
    // strip leading and ending ""
    return output.replace(/^"(.*)"$/, '$1');
  }

  parseOperandsValues(chips: ExpertChipExpression[]) {
    return map(chips, chip => {
      chip.operands = map(chip.operands, o => JSON.parse(`"${o}"`));
      return chip;
    });
  }

  public parseIntoTokens(expression: string): ExpertChipExpression[] {
    try {
      // before parsing as chips, we need to escape string content
      const exp = this.escapeSpecialJsonCharacters(expression);
      const kTree = expertGrammarParser.parse(exp, { types: ExpertGrammar });
      const explanation: ExpertChipExpression[] = kTree.explain();
      if (Array.isArray(explanation)) {
        explanation.forEach(token => {
          GrammarHelper.normalizeExpertChipExpression(token);
        });
      } else {
        GrammarHelper.normalizeExpertChipExpression(explanation);
      }
      // after chips are parsed, need to recover operands original values
      return this.parseOperandsValues(explanation);
    } catch (error) {
      return null;
    }
  }

  toExpression(chips: SimpleChipExpression[]): string {
    return GrammarHelper.toExpression(chips);
  }

  toBreadcrumbExp(chips: SimpleChipExpression[]): string {
    return GrammarHelper.toBreadcrumbExp(chips);
  }

  public toAtaBreadcrumbExp(chips: SimpleChipExpression[]): string {
    throw GrammarHelper.toAtaBreadcrumbExp(chips);
  }
}
